import type { SetupContext, VNode } from "vue";
import { cloneVNode, Comment, Fragment, h, isVNode, Text } from "vue";

/**
 * Renders its default slot without an element of its own. Lets a template drop a wrapper element in one mode and
 * keep it in another (`<component :is="wrap ? 'dt' : ListCardPassthrough">`) without repeating the content. The
 * template only binds attributes in the mode that renders an element, so it never receives any.
 */
export function ListCardPassthrough(_props: Record<string, unknown>, { slots }: SetupContext) {
  return slots.default?.();
}
ListCardPassthrough.displayName = "ListCardPassthrough";

function giveRole(nodes: unknown[], role: string): unknown[] {
  return nodes.flatMap((node) => {
    if (!isVNode(node)) {
      return [node];
    }
    if (node.type === Fragment) {
      return Array.isArray(node.children) ? giveRole(node.children, role) : [node];
    }
    if (node.type === Comment || node.type === Text) {
      return [node];
    }
    if (node.props?.role) {
      return [node];
    }
    if (typeof node.type === "string") {
      return [cloneVNode(node, { role })];
    }
    // A component may put attributes on an inner element (a checkbox passes them to its input), so it gets a
    // wrapper that carries the role instead.
    return [h("div", { role, key: node.key ?? undefined }, [node])];
  });
}

/**
 * Gives every element of its default slot an ARIA role, such as `cell` for the cells of a table row. Elements that
 * have a role of their own keep it; a component is wrapped in a `div` with the role. Without `role` the slot renders
 * as it is.
 */
export function ListCardRoleCells(props: { role?: string }, { slots }: SetupContext) {
  const nodes = slots.default?.() ?? [];
  return props.role ? (giveRole(nodes, props.role) as VNode[]) : nodes;
}
ListCardRoleCells.displayName = "ListCardRoleCells";
ListCardRoleCells.props = { role: { type: String, required: false } };
